import type { PublishedElementsEntitlementRejectedModalProps } from "@v2/types/modalTypes";
import type { AddOn as AddOnData } from "schemas/generated/addOns";
import type { BillingTier } from "schemas/generated/billing";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Button } from "@replohq/lattice/components/button/Button";
import { Tooltip } from "@replohq/lattice/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { showSubscriptionUpdateSuccessToast } from "@v2/features/billing/billingToasts";
import useApplySubscriptionChangeMutation from "@v2/features/billing/hooks/useApplySubscriptionChangeMutation";
import { useCanManageWorkspaceBilling } from "@v2/features/billing/hooks/useCanManageWorkspaceBilling";
import { getProratedCharge } from "@v2/features/billing/planPriceUtils";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "@v2/features/billing/shared/sharedBillingUtils";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useModal } from "@v2/store/modalStore";
import { billingRouter } from "@v2/trpc/routes/billing";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { DEPRECATED_formatPriceInUsdDollars } from "replo-utils/lib/format";
import {
  ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH,
  ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE,
  AddOn,
  MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES,
} from "schemas/addOns";

const PublishedElementsEntitlementRejectedModal: React.FC<
  PublishedElementsEntitlementRejectedModalProps
> = ({ explanationMessage }) => {
  const workspace = useCurrentWorkspace();
  const canManageBilling = useCanManageWorkspaceBilling();
  const modal = useModal();
  const { data: activeSubscription } = useSubscriptionDetails();

  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspace?.id && activeSubscription?.id
        ? {
            workspaceId: workspace.id,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useApplySubscriptionChangeMutation();

  const closeModal = () => {
    modal.closeModal({ type: "publishedElementsEntitlementRejectedModal" });
  };

  let headerText: string;
  let content: React.ReactNode;

  // Viewers and project guests still need to see why publishing failed, but
  // can't buy add-ons, and guests can't load the workspace's usage data.
  if (canManageBilling === false) {
    headerText = "Published items limit reached";
    content = (
      <NoBillingAccessContent
        explanationMessage={explanationMessage}
        onClose={closeModal}
      />
    );
  } else if (
    !workspace ||
    !publishedElementsUsage ||
    publishedElementsUsage.maximum === null // If they have unlimited published elements, we don't need to show this modal
  ) {
    return null;
  } else if (!activeSubscription) {
    headerText = "Upgrade to continue";
    content = (
      <FreeUserUpgradeRequiredContent
        explanationMessage={explanationMessage}
        onClose={closeModal}
        workspaceId={workspace.id}
      />
    );
  } else {
    if (!nextBillingDate) {
      return null;
    }

    const isFromEntitlement = publishedElementsUsage.isFromEntitlement;

    const currentAdditionalPublishedElementBundles =
      getCountOfAdditionalPublishedElementBundlesInSubscription(
        activeSubscription,
      );

    const hasReachedMaxBundles =
      currentAdditionalPublishedElementBundles >=
      MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES;

    const activeSubscriptionIsYearly =
      activeSubscription.billingInterval === "yearly";

    const additionalBundleFullPeriodPrice = activeSubscriptionIsYearly
      ? ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH * 12
      : ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH;

    const formattedAdditionalBundlePrice = activeSubscriptionIsYearly
      ? `${DEPRECATED_formatPriceInUsdDollars({ amount: additionalBundleFullPeriodPrice, currency: "usd" })}/year`
      : `${DEPRECATED_formatPriceInUsdDollars({ amount: additionalBundleFullPeriodPrice, currency: "usd" })}/month`;

    const formattedNextBillingDate = new Date(
      nextBillingDate,
    ).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    const proratedCharge = getProratedCharge({
      billingInterval: activeSubscription.billingInterval,
      nextBillingDate: new Date(nextBillingDate),
      fullPeriodPrice: additionalBundleFullPeriodPrice,
    });

    const handleConfirm = async () => {
      const currentAdditionalSites =
        getCountOfAdditionalSitesInSubscription(activeSubscription);
      const currentAdditionalProjects =
        getCountOfAdditionalProjectsInSubscription(activeSubscription);

      const newAdditionalPublishedElementBundles =
        currentAdditionalPublishedElementBundles + 1;

      analytics.logEvent(
        "billing.publishedElementsEntitlementRejectedModal.purchasedAddOn",
        {
          currentPlan: activeSubscription.billingTier,
          additionalPublishedElementCount:
            newAdditionalPublishedElementBundles *
            ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE,
        },
      );

      const addOns: AddOnData[] = AddOn.buildFromCounts({
        additionalSites: currentAdditionalSites,
        additionalProjects: currentAdditionalProjects,
        additionalPublishedElementBundles: newAdditionalPublishedElementBundles,
      });

      await applySubscriptionChange({
        workspaceId: workspace.id,
        billingTier: activeSubscription.billingTier,
        billingInterval: activeSubscription.billingInterval,
        addOns,
        source: "direct.billingDashboard",
      });
      showSubscriptionUpdateSuccessToast();
      closeModal();
    };

    if (activeSubscription.isManagedByReplo || isFromEntitlement) {
      headerText = "Add published items";
    } else if (hasReachedMaxBundles) {
      headerText = "Upgrade to Pro to continue";
    } else {
      headerText = "Add 20 published items";
    }

    if (activeSubscription.isManagedByReplo) {
      content = (
        <PlanManagedByReploContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
        />
      );
    } else if (isFromEntitlement) {
      content = (
        <IsFromEntitlementContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
        />
      );
    } else if (hasReachedMaxBundles) {
      content = (
        <UpgradeRequiredContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
          billingTier={activeSubscription.billingTier}
        />
      );
    } else {
      content = (
        <AddBundleContent
          explanationMessage={explanationMessage}
          formattedAdditionalBundlePrice={formattedAdditionalBundlePrice}
          formattedNextBillingDate={formattedNextBillingDate}
          proratedCharge={proratedCharge}
          newTotalPublishedElements={
            publishedElementsUsage.maximum +
            ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE
          }
          isLoading={isLoadingSubscriptionUpdate}
          onConfirm={() => void handleConfirm()}
          onClose={closeModal}
        />
      );
    }
  }

  return (
    <SplitModal
      open={true}
      title={headerText}
      media={
        <img
          src="/images/billing/published-items-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      {content}
    </SplitModal>
  );
};

const AddBundleContent = ({
  explanationMessage,
  formattedAdditionalBundlePrice,
  formattedNextBillingDate,
  proratedCharge,
  newTotalPublishedElements,
  isLoading,
  onConfirm,
  onClose,
}: {
  explanationMessage: string;
  formattedAdditionalBundlePrice: string;
  formattedNextBillingDate: string;
  proratedCharge: number;
  newTotalPublishedElements: number;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Adding{" "}
          <span className="font-bold">
            {ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE} published items
          </span>{" "}
          will increase your workspace bill by{" "}
          <span className="font-bold">{formattedAdditionalBundlePrice}</span>{" "}
          starting {formattedNextBillingDate}, bringing your total to{" "}
          <span className="font-bold">
            {newTotalPublishedElements} published items.
          </span>
          <br />
          <br /> A prorated charge of{" "}
          <span className="font-bold">
            {DEPRECATED_formatPriceInUsdDollars({
              amount: proratedCharge,
              currency: "usd",
            })}
          </span>{" "}
          will be applied today to your card on file.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <Button
          variant="primary"
          size="base"
          onClick={onConfirm}
          loading={isLoading}
        >
          Confirm
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const FreeUserUpgradeRequiredContent = ({
  explanationMessage,
  onClose,
  workspaceId,
}: {
  explanationMessage: string;
  onClose: () => void;
  workspaceId: string;
}) => {
  const navigate = useNavigate();

  const handleNavigateToBilling = () => {
    onClose();
    navigate(generatePath(routes.workspace.settings.billing, { workspaceId }));
  };

  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>You'll need to upgrade to a paid plan to continue.</div>
      </div>
      <div className="flex flex-row gap-3">
        <Button variant="primary" size="base" onClick={handleNavigateToBilling}>
          See plans
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const UpgradeRequiredContent = ({
  explanationMessage,
  onClose,
  billingTier,
}: {
  explanationMessage: string;
  onClose: () => void;
  billingTier: BillingTier;
}) => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();

  const handleNavigateToBilling = () => {
    analytics.logEvent(
      "billing.publishedElementsEntitlementRejectedModal.redirectedToBillingPage",
      {
        currentPlan: billingTier,
      },
    );

    onClose();
    navigate(
      generatePath(routes.shops.settings.workspace.billing, { projectId }),
    );
  };

  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your Starter plan has reached the maximum number of published items
          you can add.
          <br />
          <br />
          To add more published items, you'll need to upgrade to the{" "}
          <span className="font-bold">Pro plan</span>, which includes{" "}
          <span className="font-bold">unlimited published items</span>.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <Button variant="primary" size="base" onClick={handleNavigateToBilling}>
          See plans
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const PlanManagedByReploContent = ({
  explanationMessage,
  onClose,
}: {
  explanationMessage: string;
  onClose: () => void;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your plan is managed by Replo. Reach out to{" "}
          <span className="font-bold">support@replo.app</span> to add published
          items. Our team will help you adjust your plan to best suit your
          needs.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <a href="mailto:support@replo.app?subject=Managed Plan - Add Published Items">
          <Button variant="primary" size="base">
            Contact us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const NoBillingAccessContent = ({
  explanationMessage,
  onClose,
}: {
  explanationMessage: string;
  onClose: () => void;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="flex flex-col gap-4 text-sm font-normal">
        <p>{explanationMessage}</p>
        <p>
          Only workspace owners and members can add published items or change
          this workspace's plan. Ask one of them to add published items or
          unpublish an item, then try publishing again.
        </p>
      </div>
      <div className="flex flex-row gap-3">
        <Tooltip
          content={"Only workspace owners and members can add published items."}
          triggerAsChild
        >
          <span>
            <Button variant="primary" size="base" disabled>
              Add published items
            </Button>
          </span>
        </Tooltip>
        <Button variant="secondary" size="base" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

const IsFromEntitlementContent = ({
  explanationMessage,
  onClose,
}: {
  explanationMessage: string;
  onClose: () => void;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your plan's maximum number of published items has been set manually by
          the Replo team. This could be due to a custom request or a plan
          grandfathered in from our older pricing structure.
          <br />
          <br />
          Reach out to <span className="font-bold">support@replo.app</span> to
          add published items. Our team will help you adjust your plan to best
          suit your needs.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <a href="mailto:support@replo.app?subject=Custom Plan - Add Published Items">
          <Button variant="primary" size="base">
            Contact us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default PublishedElementsEntitlementRejectedModal;
